import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { PawLinkLogo } from "@/components/PawLinkLogo";
import { ArrowRight, EnvelopeSimple, Lock } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import Nav from "@/components/Nav";
import GoogleButton from "@/components/GoogleButton";
import { SUPPORT_EMAIL, supportMailto } from "@/constants/support";

export default function Login() {
    const { login } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const loc = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (!res.ok) return setError(res.error);
        nav(loc.state?.from || "/home");
    };

    return (
        <div className="min-h-screen bg-paw-bone dark:bg-paw-night">
            <Nav />

            {/* Subtle atmospheric background */}
            <div aria-hidden className="fixed inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-24 -start-24 w-[420px] h-[420px] rounded-full bg-paw-terra/10 dark:bg-paw-terra/[0.06] blur-[120px]" />
                <div className="absolute bottom-0 -end-32 w-[520px] h-[520px] rounded-full bg-paw-terra/8 dark:bg-paw-terra/[0.04] blur-[140px]" />
            </div>

            <div className="relative pt-24 sm:pt-28 pb-16 px-5 sm:px-6">
                <div className="w-full max-w-[400px] mx-auto">
                    <div className="text-center mb-8 sm:mb-10">
                        <Link to="/" className="inline-block mb-6 transition-transform hover:scale-[1.03]" data-testid="login-brand-logo">
                            <PawLinkLogo variant="stacked" size={64} />
                        </Link>
                        <h1 className="font-display text-[32px] sm:text-[38px] leading-[1.1] font-medium tracking-tight text-paw-ink dark:text-white">
                            {t("auth.welcome")}
                        </h1>
                        <p className="mt-2.5 text-[15px] text-paw-ink2 dark:text-paw-silver">
                            {t("auth.sign_sub")}
                        </p>
                    </div>

                    <div className="card-soft rounded-3xl p-6 sm:p-8 space-y-5 shadow-hover" data-testid="login-form-wrap">
                        <GoogleButton label={t("auth.google_signin")} testId="login-google-btn" />

                        <div className="flex items-center gap-4">
                            <div className="flex-1 h-px bg-paw-line dark:bg-white/10" />
                            <span className="text-[11px] uppercase tracking-[0.14em] text-paw-ink2/70 dark:text-paw-bone/40 font-semibold">
                                {t("auth.or_continue")}
                            </span>
                            <div className="flex-1 h-px bg-paw-line dark:bg-white/10" />
                        </div>

                        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                            <div>
                                <label className="label-soft flex items-center gap-1.5 text-xs uppercase tracking-wider">
                                    <EnvelopeSimple size={12} weight="duotone" /> {t("auth.email")}
                                </label>
                                <input
                                    data-testid="login-email"
                                    type="email" required autoComplete="email"
                                    className="input-soft mt-2 rounded-2xl h-12 text-[15px]"
                                    value={email} onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                />
                            </div>
                            <div>
                                <div className="flex items-center justify-between">
                                    <label className="label-soft flex items-center gap-1.5 text-xs uppercase tracking-wider">
                                        <Lock size={12} weight="duotone" /> {t("auth.password")}
                                    </label>
                                    <Link
                                        to="/forgot-password"
                                        data-testid="login-forgot-link"
                                        className="text-[11px] text-paw-forest dark:text-paw-terra hover:underline font-semibold"
                                    >
                                        {t("auth.forgot_password")}
                                    </Link>
                                </div>
                                <input
                                    data-testid="login-password"
                                    type="password" required autoComplete="current-password"
                                    className="input-soft mt-2 rounded-2xl h-12 text-[15px]"
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                />
                            </div>

                            {error && (
                                <div data-testid="login-error"
                                    className="rounded-2xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-300 px-4 py-3 text-sm">
                                    {error}
                                </div>
                            )}

                            <button
                                type="submit" disabled={loading}
                                data-testid="login-submit"
                                className="btn-primary w-full h-12 rounded-2xl text-[15px] tracking-tight disabled:opacity-60"
                            >
                                {loading ? t("auth.signing") : t("auth.signin")}
                                <ArrowRight size={16} className="ms-1.5" />
                            </button>
                        </form>
                    </div>

                    <p className="text-center text-sm text-paw-ink2 dark:text-paw-bone/60 mt-6">
                        {t("auth.no_account")}{" "}
                        <Link to="/register" data-testid="login-goto-register"
                            className="text-paw-forest dark:text-paw-terra font-semibold hover:underline underline-offset-4">
                            {t("auth.create_one")}
                        </Link>
                    </p>
                    <p className="text-center text-[11px] text-paw-ink2/70 dark:text-paw-bone/50 mt-3" data-testid="login-support-help">
                        {t("auth.trouble_signing_in")}{" "}
                        <a
                            href={supportMailto({ subject: "PawLink Support Request — Account Access" })}
                            className="text-paw-forest dark:text-paw-terra hover:underline font-medium"
                            dir="ltr"
                        >
                            {SUPPORT_EMAIL}
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
}
